import { authenticatedFetch } from './authApi';
import { getEnv } from '../utils/env';


export type SeasonStatus = 'concept' | 'active' | 'completed';

export interface Season {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: SeasonStatus;
  created_at: string;
  updated_at: string;
}

export interface SeasonInput {
  name: string;
  start_date: string;
  end_date: string;
}

const apiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const request = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const response = await authenticatedFetch(`${apiBaseUrl()}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload && typeof payload === 'object' && 'detail' in payload
      ? String((payload as { detail?: unknown }).detail || '')
      : '';
    throw new Error(detail || `Season request failed (${response.status})`);
  }
  return payload;
};

export const listSeasonsApi = async (): Promise<Season[]> => {
  const payload = await request('/api/v1/seasons');
  return payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
    ? (payload as { data: Season[] }).data
    : [];
};

export const createSeasonApi = async (input: SeasonInput): Promise<Season> => {
  const payload = await request('/api/v1/seasons', { method: 'POST', body: JSON.stringify(input) });
  return (payload as { data: Season }).data;
};

export const updateSeasonApi = async (seasonId: string, input: SeasonInput): Promise<Season> => {
  const payload = await request(`/api/v1/seasons/${encodeURIComponent(seasonId)}`, {
    method: 'PATCH', body: JSON.stringify(input),
  });
  return (payload as { data: Season }).data;
};

export const activateSeasonApi = async (seasonId: string): Promise<Season> => {
  const payload = await request(`/api/v1/seasons/${encodeURIComponent(seasonId)}/activate`, { method: 'POST' });
  return (payload as { data: Season }).data;
};

export const completeSeasonApi = async (seasonId: string): Promise<Season> => {
  const payload = await request(`/api/v1/seasons/${encodeURIComponent(seasonId)}/complete`, { method: 'POST' });
  return (payload as { data: Season }).data;
};

export const deleteSeasonApi = async (seasonId: string): Promise<void> => {
  await request(`/api/v1/seasons/${encodeURIComponent(seasonId)}`, { method: 'DELETE' });
};
